// NEW_FILE_JUSTIFIED: New component for full-size media viewing — no existing component handles image/video lightbox display with download capability.

import { memo, useCallback } from 'react';
import { Download, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { IMediaLightboxProps } from '@/@types/messages';

export const MediaLightbox = memo(function MediaLightbox({
  open,
  onOpenChange,
  mediaUrl,
  mediaType,
  filename,
}: IMediaLightboxProps) {
  const handleDownload = useCallback(() => {
    const defaultName = mediaType === 'image' ? 'image.jpg' : 'video.mp4';
    fetch(mediaUrl)
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename ?? defaultName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        const link = document.createElement('a');
        link.href = mediaUrl;
        link.download = filename ?? defaultName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
  }, [mediaUrl, filename, mediaType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] max-h-[90vh] p-0 border-none bg-black/95 overflow-hidden"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">
          {mediaType === 'image' ? 'Image preview' : 'Video player'}
        </DialogTitle>

        <div className="absolute top-3 right-3 z-10 flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20 rounded-full"
            onClick={handleDownload}
            aria-label="Download file"
          >
            <Download className="size-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20 rounded-full"
            onClick={() => onOpenChange(false)}
            aria-label="Close preview"
          >
            <X className="size-5" />
          </Button>
        </div>

        <div className="flex items-center justify-center min-h-[50vh]">
          {mediaType === 'image' ? (
            <img
              src={mediaUrl}
              alt={filename ?? 'Image attachment'}
              className="max-w-full max-h-[85vh] object-contain"
            />
          ) : (
            <video
              src={mediaUrl}
              controls
              autoPlay
              className="max-w-full max-h-[85vh]"
              aria-label={filename ?? 'Video player'}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});
